import pickBy from 'lodash/pickBy'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import {
  GeckoPolygonalPlate,
  GeckoPolygonalPlateJSON,
} from 'types/assetModel/features/geckoPolygonalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { mergeMaps } from 'utils/mergeMaps'
import { requiredStringSchema } from 'utils/zodHelpers'

type ShipHullSupportedFeatures =
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON
  | GeckoPlateJSON
  | GeckoPolygonalPlateJSON

export type ShipHullAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.BasicShipHull
  model_id: string
  features: Record<string, ShipHullSupportedFeatures>
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
  geometry_version?: string
}

export const ShipHullAvailableFeatures: GeckoFeatureModelType[] = []

export const NewShipHullAssetModelJSON = {
  name: 'New Ship Hull',
  type: GeckoComponentModelType.BasicShipHull,
  model_id: '',
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
} as ShipHullAssetModelJSON

export class GeckoShipHull extends GeckoEditableComponent {
  modelID: string
  type: GeckoComponentModelType.BasicShipHull
  componentBounds: GeckoComponentRect
  sceneConfig = undefined
  plates: Map<string, GeckoPlate>
  polygonalPlates: Map<string, GeckoPolygonalPlate>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  verticalWelds: Map<string, GeckoVerticalWeld>
  features: Map<
    string,
    GeckoPlate | GeckoPolygonalPlate | GeckoHorizontalWeld | GeckoVerticalWeld
  >

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    modelID: string,
    componentBounds: GeckoComponentRect,
    plates: Map<string, GeckoPlate>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    polygonalPlates: Map<string, GeckoPolygonalPlate>
  ) {
    super(id, name, json)
    this.modelID = modelID
    this.type = GeckoComponentModelType.BasicShipHull
    this.componentBounds = componentBounds

    this.plates = plates
    this.polygonalPlates = polygonalPlates
    this.horizontalWelds = horizontalWelds
    this.verticalWelds = verticalWelds

    this.features = mergeMaps(
      plates,
      horizontalWelds,
      verticalWelds,
      polygonalPlates
    )
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get polygonalPlatesArray() {
    return Array.from(this.polygonalPlates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public static CreateFromJSON(id: string, json: ShipHullAssetModelJSON) {
    const name = json.name as string
    const modelID = json.model_id as string

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const featureData = json.features as Record<
      string,
      ShipHullSupportedFeatures
    >

    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )
    const polygonalPlates = new Map(
      Object.entries(featureData).flatMap(([plateID, feature]) =>
        feature.type === GeckoFeatureModelType.PolygonalPlate
          ? [
              [
                plateID,
                GeckoPolygonalPlate.CreateFromJSON(id, plateID, feature),
              ] as const,
            ]
          : []
      )
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    return new GeckoShipHull(
      id,
      json,
      name,
      modelID,
      componentBounds,
      plates,
      horizontalWelds,
      verticalWelds,
      polygonalPlates
    )
  }

  public asJSON(): ShipHullAssetModelJSON {
    return generateShipHull(
      this.name,
      this.modelID,
      this.json as ShipHullAssetModelJSON
    )
  }
}

const generateShipHull = (
  name: string,
  modelID: string,
  otherShipHullData: ShipHullAssetModelJSON = NewShipHullAssetModelJSON
): ShipHullAssetModelJSON => {
  return {
    name: name,
    type: GeckoComponentModelType.BasicShipHull,
    model_id: modelID,
    features: otherShipHullData.features,
    scene_config: otherShipHullData.scene_config,
    component_bounds: otherShipHullData.component_bounds,
    geometry_version: otherShipHullData.geometry_version,
  }
}

export const ShipHullAssetModelFormFields = (
  ShipHullJSON: ShipHullAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Ship Hull Name',
      value: ShipHullJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'model_id',
      label: 'Model Entry ID',
      value: ShipHullJSON.model_id,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
  ]
}

export const ShipHullJSONFromFormFields = (
  component: ShipHullAssetModelJSON,
  formFields: FormField[]
) => {
  return generateShipHull(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    getFormFieldByDataKey(formFields, 'model_id')?.value as string,
    component
  )
}
